import { PDFArray, PDFDict, PDFDocument, PDFName, StandardFonts } from 'pdf-lib';
import {
  calculateLongFormMeansTest,
  createDefaultLongFormData,
  longFormMoney,
  mergeLongFormData,
} from '../../utils/longFormMeansTest';
import { loadMeansTestStandards } from '../../utils/meansTestStandards';
import { filingDebtors as getFilingDebtors } from '../../utils/debtors';

const templateUrl = chapter => chapter === '7'
  ? '/forms/b_122a-2_0425-form-12.pdf'
  : '/forms/b_122c-2_0425-form-14.pdf';

const fetchPdf = async url => {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Failed to load long-form template: ${url}`);
  return new Uint8Array(await response.arrayBuffer());
};

const fullName = debtor => [debtor?.first_name, debtor?.second_name, debtor?.last_name, debtor?.suffix]
  .filter(Boolean).join(' ');

const signatureDate = (caseData, amended) => {
  const value = amended ? new Date().toISOString().slice(0, 10) : caseData?.execution_date;
  if (!value) return '';
  const [year, month, day] = String(value).slice(0, 10).split('-');
  return `${month}/${day}/${year}`;
};

const formDate = value => {
  if (!value) return '';
  const [year, month, day] = String(value).slice(0, 10).split('-');
  return `${month}/${day}/${year}`;
};

const numberValue = value => {
  const parsed = Number(String(value ?? '').replace(/[$,]/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
};

const createDrawer = (font) => {
  const text = (page, rect, value, options = {}) => {
    const stringValue = value === null || value === undefined ? '' : String(value);
    if (!stringValue) return;
    const [rawX1, rawY1, rawX2, rawY2] = rect;
    const x1 = Math.min(rawX1, rawX2);
    const y1 = Math.min(rawY1, rawY2);
    const x2 = Math.max(rawX1, rawX2);
    const y2 = Math.max(rawY1, rawY2);
    const width = Math.max(1, x2 - x1 - 3);
    const height = Math.max(1, y2 - y1 - 2);
    const requestedSize = options.size || 8;
    const widthAtRequestedSize = font.widthOfTextAtSize(stringValue, requestedSize);
    const size = Math.max(5, Math.min(requestedSize, height, widthAtRequestedSize > width
      ? requestedSize * (width / widthAtRequestedSize)
      : requestedSize));
    const renderedWidth = font.widthOfTextAtSize(stringValue, size);
    const x = options.align === 'right' ? x2 - renderedWidth - 2 : x1 + 2;
    const y = y1 + Math.max(1, (height - size) / 2) + 1;
    page.drawText(stringValue, { x, y, size, font });
  };

  const amount = (page, rect, value) => text(page, rect, longFormMoney(value), { align: 'right', size: 8 });
  const check = (page, rect, checked = true) => {
    if (checked) text(page, rect, 'X', { size: 9 });
  };
  return { text, amount, check };
};

const removeAcroForm = pdf => {
  const subtypeName = PDFName.of('Subtype');
  const annotationsName = PDFName.of('Annots');

  // The official 04/25 court PDFs contain duplicate and unnamed controls whose
  // appearance streams cannot be flattened reliably by pdf-lib. Remove only
  // widget annotations at the object level, preserving links and page content.
  pdf.getPages().forEach(page => {
    const annotations = page.node.lookupMaybe(annotationsName, PDFArray);
    if (!annotations) return;
    const retained = annotations.asArray().filter(reference => {
      const annotation = pdf.context.lookupMaybe(reference, PDFDict);
      return annotation?.get(subtypeName)?.toString() !== '/Widget';
    });
    if (retained.length) page.node.set(annotationsName, pdf.context.obj(retained));
    else page.node.delete(annotationsName);
  });
  pdf.catalog.delete(PDFName.of('AcroForm'));
};

const wrapText = (font, value, size, maxWidth) => {
  const paragraphs = String(value || '').split(/\n/);
  const lines = [];
  paragraphs.forEach(paragraph => {
    const words = paragraph.trim().split(/\s+/).filter(Boolean);
    if (!words.length) {
      lines.push('');
      return;
    }
    let line = '';
    words.forEach(word => {
      const candidate = line ? `${line} ${word}` : word;
      if (line && font.widthOfTextAtSize(candidate, size) > maxWidth) {
        lines.push(line);
        line = word;
      } else {
        line = candidate;
      }
    });
    if (line) lines.push(line);
  });
  return lines;
};

const appendContinuationPages = ({
  pdf, font, boldFont, caseData, chapter, data, calculation, maritalAdjustments,
}) => {
  const entries = [];
  const add = (line, description, amount = null) => {
    if (String(description || '').trim()) entries.push({ line, description, amount });
  };

  (maritalAdjustments || []).slice(3).forEach(row =>
    add('3', row.description || 'Additional marital adjustment', row.amount)
  );
  if (String(data.housing_adjustment_explanation || '').length > 85) {
    add('10', `Full additional housing adjustment explanation: ${data.housing_adjustment_explanation}`, data.housing_adjustment_amount);
  }

  const securedOverflow = [
    ['9b', calculation.homeDebts.slice(3)],
    ['13b', calculation.vehicle1Debts.slice(2)],
    ['13e', calculation.vehicle2Debts.slice(2)],
    ['33d', calculation.otherSecuredDebts.slice(3)],
  ];
  securedOverflow.forEach(([line, rows]) => rows.forEach(row => add(
    line,
    [row.creditor_name, row.property_description, row.includes_taxes_insurance ? 'payment includes taxes/insurance' : 'payment does not include taxes/insurance']
      .filter(Boolean).join(' — '),
    row.average_monthly_payment
  )));

  const cureRows = (data.secured_debts || []).filter(row => numberValue(row.cure_total) > 0);
  cureRows.slice(3).forEach(row => add(
    '34',
    `${row.creditor_name || 'Secured creditor'} — ${row.property_description || 'secured property'}; total cure amount ${longFormMoney(row.cure_total)}`,
    numberValue(row.cure_total) / 60
  ));

  const specialLimit = chapter === '7' ? 4 : 3;
  (data.special_circumstances || []).forEach((row, index) => {
    if (index >= specialLimit || String(row.description || '').length > 70) {
      add('43', row.description || 'Additional special circumstance', row.amount);
    }
  });
  if (chapter === '13') {
    (data.income_expense_changes || []).slice(4).forEach(row => add(
      '46',
      `${row.form || '122C-2'} line ${row.line || '—'}; ${row.reason || 'change in income or expenses'}; ${formDate(row.date) || 'date not specified'}; ${row.direction || 'increase'}`,
      row.amount
    ));
  }
  if (!entries.length) return;

  const filingDebtors = getFilingDebtors(caseData?.debtors);
  const debtorNames = filingDebtors.map(fullName).filter(Boolean).join(' and ');
  const title = `Official Form ${chapter === '7' ? '122A-2' : '122C-2'} — Continuation Sheet`;
  let page;
  let y;
  let pageNumber = 0;

  const addPage = () => {
    page = pdf.addPage([612, 792]);
    pageNumber += 1;
    page.drawText(title, { x: 54, y: 742, size: 14, font: boldFont });
    page.drawText(`Debtor(s): ${debtorNames}`, { x: 54, y: 719, size: 9, font });
    page.drawText(`Case number: ${caseData?.case_number || ''}`, { x: 360, y: 719, size: 9, font });
    page.drawText(`Attachment page ${pageNumber}`, { x: 54, y: 700, size: 8, font });
    page.drawText('Line', { x: 54, y: 677, size: 9, font: boldFont });
    page.drawText('Additional information', { x: 95, y: 677, size: 9, font: boldFont });
    page.drawText('Monthly amount', { x: 482, y: 677, size: 9, font: boldFont });
    y = 657;
  };

  addPage();
  entries.forEach(entry => {
    const lines = wrapText(font, entry.description, 9, 365);
    const rowHeight = Math.max(25, lines.length * 12 + 10);
    if (y - rowHeight < 58) addPage();
    page.drawText(String(entry.line), { x: 58, y: y - 2, size: 9, font });
    lines.forEach((line, index) => {
      page.drawText(line, { x: 95, y: y - (index * 12) - 2, size: 9, font });
    });
    if (entry.amount !== null && entry.amount !== undefined) {
      const amount = `$${longFormMoney(entry.amount)}`;
      page.drawText(amount, {
        x: 568 - font.widthOfTextAtSize(amount, 9), y: y - 2, size: 9, font,
      });
    }
    y -= rowHeight;
  });
};

const drawChapter7 = ({ pdf, draw, caseData, data, calculation, maritalAdjustments, amended }) => {
  const pages = pdf.getPages();
  const primary = caseData?.debtors?.find(debtor => debtor.is_primary && debtor.is_filing)
    || caseData?.debtors?.find(debtor => debtor.is_filing);
  const secondary = caseData?.debtors?.find(debtor => !debtor.is_primary && debtor.is_filing);
  const debtor1 = fullName(primary);
  const debtor2 = fullName(secondary);
  const caseNumber = caseData?.case_number || '';
  const date = signatureDate(caseData, amended);

  draw.text(pages[0], [88.4, 701.8, 343, 715.6], debtor1);
  draw.text(pages[0], [88.2, 682.5, 342, 696.3], debtor2);
  draw.text(pages[0], [166.5, 654, 207, 671.3], 'Eastern', { size: 7 });
  draw.text(pages[0], [251, 654, 328.6, 671.3], 'Tennessee', { size: 7 });
  draw.text(pages[0], [89.6, 639.2, 259, 653], caseNumber);
  draw.check(pages[0], amended ? [421.2, 631.1, 431, 641.6] : [0, 0, 0, 0], amended);

  pages.slice(1).forEach(page => {
    draw.text(page, [82, 748, 300, 762], debtor1);
    draw.text(page, [431, 748, 575, 762], caseNumber);
  });

  draw.check(pages[0], calculation.chapter7Presumption
    ? [420.8, 659.1, 430.6, 669.6]
    : [420.8, 676.7, 430.6, 687.2]);
  draw.amount(pages[0], [534.5, 435.3, 584, 449], calculation.combinedMonthly);
  const hasSecondColumn = !!(caseData?.debtors || []).find(debtor => !debtor.is_primary);
  draw.check(pages[0], hasSecondColumn ? [48.2, 380.6, 58.1, 391.2] : [48.2, 395.8, 58.1, 406.3]);
  if (hasSecondColumn) draw.check(pages[0], secondary ? [61.4, 347.5, 71.3, 358] : [61.4, 362.8, 71.3, 373.3]);
  const adjustments = maritalAdjustments || [];
  draw.check(pages[0], adjustments.length ? [48.2, 249.8, 58.1, 260.4] : [48.2, 265.1, 58.1, 275.6]);
  const adjustmentRows = [
    [[68.5, 182.6, 295.3, 196.2], [331.9, 182.6, 394.2, 196.2]],
    [[68.5, 156.2, 295.3, 169.8], [331.8, 156.2, 394.1, 169.8]],
    [[68.5, 133.1, 295.3, 146.8], [331.9, 133.1, 394.2, 146.8]],
  ];
  adjustmentRows.forEach(([descriptionRect, amountRect], index) => {
    const item = adjustments[index];
    if (!item) return;
    draw.text(pages[0], descriptionRect, item.description || 'Marital adjustment');
    draw.amount(pages[0], amountRect, item.amount);
  });
  draw.amount(pages[0], [331.9, 109.4, 394.2, 123], calculation.maritalAdjustment);
  draw.amount(pages[0], [532.7, 103.4, 595, 117], calculation.maritalAdjustment);
  draw.amount(pages[0], [532.6, 68.1, 582.7, 81.8], calculation.adjustedMonthlyIncome);

  draw.text(pages[1], [460, 515.3, 523.8, 529.1], calculation.householdSize);
  draw.amount(pages[1], [536.6, 446.3, 578.4, 460.1], calculation.line6);
  draw.amount(pages[1], [266.9, 309.5, 320.2, 323.3], calculation.healthUnder65);
  draw.text(pages[1], [266.9, 275.8, 293.6, 289.6], calculation.under65);
  draw.amount(pages[1], [266.9, 251.6, 320.3, 265.4], calculation.line7c);
  draw.amount(pages[1], [396, 251.7, 449.4, 265.5], calculation.line7c);
  draw.amount(pages[1], [266.9, 175.8, 320.2, 189.6], calculation.healthOver65);
  draw.text(pages[1], [266.9, 154.6, 293.6, 168.4], calculation.over65);
  draw.amount(pages[1], [266.9, 130.5, 320.3, 144.3], calculation.line7f);
  draw.amount(pages[1], [395.5, 123.8, 448.9, 137.6], calculation.line7f);
  draw.amount(pages[1], [396.5, 89.1, 445.4, 102.9], calculation.line7g);
  draw.amount(pages[1], [533, 84.8, 582, 98.6], calculation.line7g);

  draw.amount(pages[2], [522.7, 573.6, 576, 587.4], calculation.line8);
  draw.amount(pages[2], [426.4, 523, 475.3, 536.8], calculation.line9a);
  const homeRows = calculation.homeDebts.slice(0, 3);
  const homeRects = [
    [[80.4, 398.4, 272, 412.2], [306.8, 398.4, 360.1, 412.2]],
    [[80.4, 375.9, 272, 389.7], [306.7, 375.9, 360.1, 389.7]],
    [[80.4, 349.6, 272, 363.4], [306.8, 349.6, 360.1, 363.4]],
  ];
  homeRects.forEach(([nameRect, amountRect], index) => {
    const row = homeRows[index];
    if (!row) return;
    draw.text(pages[2], nameRect, row.creditor_name);
    draw.amount(pages[2], amountRect, row.average_monthly_payment);
  });
  draw.amount(pages[2], [306.8, 322, 360.1, 335.8], calculation.line9b);
  draw.amount(pages[2], [425.1, 322.2, 478.4, 336], calculation.line9b);
  draw.amount(pages[2], [427, 279.2, 475.8, 293], calculation.line9c);
  draw.amount(pages[2], [522.7, 280.7, 571.6, 294.5], calculation.line9c);
  draw.amount(pages[2], [522.7, 233.2, 571.6, 247], calculation.line10);
  draw.text(pages[2], [105.8, 207.6, 495.8, 221.4], data.housing_adjustment_explanation || '');
  draw.check(pages[2], calculation.vehicleCount === 0
    ? [61.2, 147.8, 72.2, 157.8]
    : calculation.vehicleCount === 1
      ? [61.2, 136.3, 72.2, 146.3]
      : [61.2, 124.5, 72.2, 134.5]);
  draw.amount(pages[2], [522.7, 76, 571.6, 89.8], calculation.line12);

  const vehicles = (caseData?.vehicles || []).filter(vehicle => !vehicle.is_recreational).slice(0, 2);
  const vehicleName = vehicle => [vehicle?.year, vehicle?.make, vehicle?.model].filter(Boolean).join(' ');
  const vehicle1Description = calculation.line13b > 0
    ? vehicleName(vehicles[0]) || calculation.vehicle1Debts[0]?.property_description || 'Vehicle 1'
    : '';
  const vehicle2Description = calculation.line13e > 0
    ? vehicleName(vehicles[1]) || calculation.vehicle2Debts[0]?.property_description || 'Vehicle 2'
    : '';
  draw.text(pages[3], [190, 662.1, 470.2, 675.9], vehicle1Description);
  draw.amount(pages[3], [418.8, 623.2, 467.6, 637], calculation.line13a);
  const vehicle1Rects = [
    [[90.5, 513, 255, 526.8], [284.6, 513, 337.9, 526.8]],
    [[90.5, 488.1, 255, 501.9], [284.6, 488.1, 337.9, 501.9]],
  ];
  vehicle1Rects.forEach(([nameRect, amountRect], index) => {
    const row = calculation.vehicle1Debts[index];
    if (!row) return;
    draw.text(pages[3], nameRect, row.creditor_name);
    draw.amount(pages[3], amountRect, row.average_monthly_payment);
  });
  draw.amount(pages[3], [284.6, 462.1, 337.9, 475.9], calculation.line13b);
  draw.amount(pages[3], [413.9, 461.3, 467.1, 475.1], calculation.line13b);
  draw.amount(pages[3], [413.5, 417.4, 466.8, 431.2], calculation.line13c);
  draw.amount(pages[3], [538.9, 409.7, 592.2, 423.5], calculation.line13c);
  draw.text(pages[3], [190, 374.8, 470.2, 388.6], vehicle2Description);
  draw.amount(pages[3], [413.5, 330, 466.8, 343.8], calculation.line13d);
  const vehicle2Rects = [
    [[90.5, 250.7, 255, 264.5], [284.6, 250.7, 337.9, 264.5]],
    [[90.5, 217.2, 255, 231], [284.6, 217.2, 337.9, 231]],
  ];
  vehicle2Rects.forEach(([nameRect, amountRect], index) => {
    const row = calculation.vehicle2Debts[index];
    if (!row) return;
    draw.text(pages[3], nameRect, row.creditor_name);
    draw.amount(pages[3], amountRect, row.average_monthly_payment);
  });
  draw.amount(pages[3], [284.6, 184.5, 337.9, 198.3], calculation.line13e);
  draw.amount(pages[3], [413.1, 184.3, 466.3, 198.1], calculation.line13e);
  draw.amount(pages[3], [413.5, 141.8, 466.8, 155.6], calculation.line13f);
  draw.amount(pages[3], [539.1, 132.3, 592.3, 146.1], calculation.line13f);
  draw.amount(pages[3], [538.6, 97.8, 591.8, 111.6], calculation.line14);
  draw.amount(pages[3], [539.8, 57.4, 593, 71.2], calculation.line15);

  const necessaryKeys = ['taxes', 'involuntary_deductions', 'life_insurance', 'court_ordered_payments', 'education', 'childcare', 'additional_health_care', 'optional_telephones'];
  const necessaryRects = [
    [539.8, 663.8, 578.3, 677.6], [539.8, 594.7, 578.3, 608.5],
    [539.8, 545.6, 578.3, 559.4], [539.8, 504.4, 578.3, 518.2],
    [539.8, 456.6, 578.3, 470.4], [539.8, 422.1, 578.3, 435.9],
    [539.8, 370.3, 578.3, 384.1], [539.6, 321.9, 578.2, 335.7],
  ];
  necessaryKeys.forEach((key, index) => draw.amount(pages[4], necessaryRects[index], data[key]));
  draw.amount(pages[4], [539.8, 266.7, 578.3, 280.5], calculation.line24);

  draw.amount(pages[5], [262.2, 640.2, 315.5, 654], data.health_insurance);
  draw.amount(pages[5], [262.2, 621.8, 315.5, 635.6], data.disability_insurance);
  draw.amount(pages[5], [262.2, 603.8, 315.5, 617.6], data.health_savings_account);
  draw.amount(pages[5], [262.2, 585.2, 315.5, 599], calculation.healthInsuranceTotal);
  draw.amount(pages[5], [539.7, 584.3, 592.9, 598.1], calculation.line25);
  draw.check(pages[5], data.actually_spend_health_total === false
    ? [61.2, 544.3, 72.3, 555]
    : [61.2, 531.1, 72.3, 541.8]);
  if (data.actually_spend_health_total === false) draw.amount(pages[5], [263, 540.5, 311.9, 554.3], data.actual_health_total);
  const additionalKeys = ['family_support_contributions', 'family_violence', 'additional_home_energy', 'dependent_education', 'additional_food_clothing', 'charitable_contributions'];
  const additionalRects = [
    [539.8, 489.4, 593, 503.2], [539.8, 435.2, 593, 449], [539.8, 362.7, 593, 376.5],
    [539.8, 295.6, 593, 309.4], [539.8, 232, 593, 245.8], [539.8, 147.3, 593, 161.1],
  ];
  additionalKeys.forEach((key, index) => draw.amount(pages[5], additionalRects[index], key === 'additional_food_clothing' ? calculation.line30 : data[key]));
  draw.amount(pages[5], [539.8, 106.9, 593, 120.7], calculation.line32);

  draw.amount(pages[6], [409, 590.7, 469.7, 604.5], calculation.line9b);
  draw.amount(pages[6], [409, 549.9, 469.7, 563.7], calculation.line13b);
  draw.amount(pages[6], [409, 528.5, 469.7, 542.3], calculation.line13e);
  const otherRows = calculation.otherSecuredDebts.slice(0, 3);
  const otherRects = [
    [[92.3, 446.8, 213, 460.6], [230.4, 446.8, 323.9, 460.6], [342.8, 452.4, 353.3, 464.3], [342.8, 440.4, 353.3, 452.3], [409, 446.8, 469.7, 460.6]],
    [[92.3, 414.6, 212.9, 428.4], [230.4, 414.6, 323.9, 428.4], [342.8, 420.4, 353.3, 432.3], [342.8, 408.4, 353.3, 420.3], [409, 414.6, 469.7, 428.4]],
    [[92.3, 382.1, 212.9, 395.9], [230.4, 382.1, 323.9, 395.9], [342.8, 388.4, 353.3, 400.3], [342.8, 376.4, 353.3, 388.3], [409, 382.1, 469.7, 395.9]],
  ];
  otherRects.forEach(([nameRect, propertyRect, noRect, yesRect, amountRect], index) => {
    const row = otherRows[index];
    if (!row) return;
    draw.text(pages[6], nameRect, row.creditor_name);
    draw.text(pages[6], propertyRect, row.property_description);
    draw.check(pages[6], row.includes_taxes_insurance ? yesRect : noRect);
    draw.amount(pages[6], amountRect, row.average_monthly_payment);
  });
  draw.amount(pages[6], [409, 353.4, 469.7, 367.2], calculation.line33e);
  draw.amount(pages[6], [540.3, 351.3, 593.6, 365.1], calculation.line33e);
  const cureRows = (data.secured_debts || []).filter(row => numberValue(row.cure_total) > 0).slice(0, 3);
  draw.check(pages[6], cureRows.length ? [61.1, 277.3, 73.4, 289.8] : [61.1, 290.7, 73.4, 303.2]);
  const cureRects = [
    [[95.9, 217.6, 185.4, 231.4], [199.1, 217.6, 277, 231.4], [295.7, 217.6, 340.1, 231.4], [409, 217.6, 466.8, 231.4]],
    [[95.9, 196, 185.4, 209.8], [199.1, 196, 277, 209.8], [295.7, 196, 340.1, 209.8], [409, 196, 466.8, 209.8]],
    [[95.9, 174.7, 185.4, 188.5], [199.1, 174.7, 277, 188.5], [295.7, 174.7, 340.1, 188.5], [409, 174.7, 466.8, 188.5]],
  ];
  cureRects.forEach(([nameRect, propertyRect, totalRect, monthlyRect], index) => {
    const row = cureRows[index];
    if (!row) return;
    draw.text(pages[6], nameRect, row.creditor_name);
    draw.text(pages[6], propertyRect, row.property_description);
    draw.amount(pages[6], totalRect, row.cure_total);
    draw.amount(pages[6], monthlyRect, numberValue(row.cure_total) / 60);
  });
  draw.amount(pages[6], [409, 151.1, 466.8, 164.9], calculation.line34);
  draw.amount(pages[6], [540.3, 150.4, 593.6, 164.2], calculation.line34);
  draw.check(pages[6], calculation.priorityClaimTotal > 0 ? [61.1, 83.6, 73.4, 96.1] : [61.1, 96.7, 73.4, 109.2]);
  draw.amount(pages[6], [409, 54, 462.2, 67.8], calculation.priorityClaimTotal);
  draw.amount(pages[6], [540.3, 50.3, 593.6, 64.1], calculation.line35);

  draw.check(pages[7], data.eligible_for_chapter_13 === false ? [60.6, 682.1, 72.9, 693.4] : [60.6, 667.2, 72.9, 678.5]);
  if (data.eligible_for_chapter_13 !== false) {
    draw.amount(pages[7], [408.8, 647.4, 466.7, 661.2], data.projected_chapter_13_payment);
    draw.text(pages[7], [409.1, 600.6, 435.6, 614.4], longFormMoney(calculation.adminMultiplier));
    draw.amount(pages[7], [408.8, 553.5, 466.7, 567.3], calculation.line36);
    draw.amount(pages[7], [539.2, 552.5, 597.1, 566.3], calculation.line36);
  }
  draw.amount(pages[7], [538.6, 516, 578.6, 529.8], calculation.line37);
  draw.amount(pages[7], [285.4, 432.3, 347.6, 446.1], calculation.line24);
  draw.amount(pages[7], [285.4, 408.1, 347.6, 421.9], calculation.line32);
  draw.amount(pages[7], [285.4, 382.8, 347.6, 396.6], calculation.line37);
  draw.amount(pages[7], [285.4, 361.3, 347.6, 375.1], calculation.line38);
  draw.amount(pages[7], [538.3, 360.6, 600.5, 374.4], calculation.line38);
  draw.amount(pages[7], [284.4, 291.7, 346.7, 305.3], calculation.adjustedMonthlyIncome);
  draw.amount(pages[7], [285.1, 267.9, 347.4, 281.7], calculation.line38);
  draw.amount(pages[7], [283.8, 240.8, 346.1, 258.2], calculation.line39c);
  draw.amount(pages[7], [436.3, 240.6, 498.5, 258], calculation.line39c);
  draw.amount(pages[7], [434.7, 189.6, 488.1, 203.4], calculation.line39d);
  draw.amount(pages[7], [540.8, 181.8, 594.2, 195.6], calculation.line39d);
  const resultRect = calculation.chapter7Result === 'no_presumption_low'
    ? [60.6, 135, 72.9, 146.2]
    : calculation.chapter7Result === 'presumption_high'
      ? [60.6, 105.1, 72.9, 116.3]
      : [60.6, 71.2, 72.9, 82.4];
  draw.check(pages[7], resultRect);

  draw.amount(pages[8], [433, 679.9, 481.8, 693.7], calculation.unsecuredTotal);
  draw.amount(pages[8], [433, 634, 481.8, 647.8], calculation.line41b);
  draw.amount(pages[8], [544.4, 635.5, 593.2, 649.3], calculation.line41b);
  if (calculation.chapter7UsesDebtTest) {
    const line42Rect = calculation.chapter7Line42Result === 'presumption'
      ? [59.8, 517.8, 71.4, 530.2]
      : [59.8, 551.4, 71.4, 563.7];
    draw.check(pages[8], line42Rect);
  }
  const special = (data.special_circumstances || []).slice(0, 4);
  draw.check(pages[8], special.length ? [48.2, 391.2, 59.9, 403.6] : [48.2, 406.2, 59.9, 418.6]);
  const specialRects = [
    [[82.4, 283.8, 389.9, 297.6], [433, 283.8, 513, 297.6]],
    [[82.4, 259.5, 389.9, 273.3], [433, 259.5, 513, 273.3]],
    [[82.4, 233.8, 389.9, 247.6], [433, 233.8, 513, 247.6]],
    [[82.4, 210.2, 389.9, 224], [433, 210.2, 513, 224]],
  ];
  specialRects.forEach(([descriptionRect, amountRect], index) => {
    const row = special[index];
    if (!row) return;
    draw.text(pages[8], descriptionRect, row.description);
    draw.amount(pages[8], amountRect, row.amount);
  });
  draw.text(pages[8], [91.4, 105.7, 303.4, 119.9], debtor1 ? `/s/ ${debtor1}` : '');
  draw.text(pages[8], [337.8, 105.2, 565.8, 119.5], debtor2 ? `/s/ ${debtor2}` : '');
  draw.text(pages[8], [109.1, 67.5, 177.2, 81.8], date);
  draw.text(pages[8], [356.8, 67.5, 425, 81.8], debtor2 ? date : '');
};

const drawChapter13 = ({ pdf, draw, caseData, data, calculation, amended }) => {
  const pages = pdf.getPages();
  const primary = caseData?.debtors?.find(debtor => debtor.is_primary && debtor.is_filing)
    || caseData?.debtors?.find(debtor => debtor.is_filing);
  const secondary = caseData?.debtors?.find(debtor => !debtor.is_primary && debtor.is_filing);
  const debtor1 = fullName(primary);
  const debtor2 = fullName(secondary);
  const caseNumber = caseData?.case_number || '';
  const date = signatureDate(caseData, amended);

  draw.text(pages[0], [85.9, 709.4, 342.8, 723.5], debtor1);
  draw.text(pages[0], [85.9, 689, 342.8, 703.1], debtor2);
  draw.text(pages[0], [165.2, 659.1, 205.5, 676.4], 'Eastern', { size: 7 });
  draw.text(pages[0], [249.5, 659.1, 327.4, 676.4], 'Tennessee', { size: 7 });
  draw.text(pages[0], [86.9, 643.7, 257.3, 657.8], caseNumber);
  draw.check(pages[0], [416.3, 628.1, 427.4, 638.9], amended);
  pages.slice(1).forEach(page => {
    draw.text(page, [80, 748, 337, 763], debtor1);
    draw.text(page, [430, 748, 598, 763], caseNumber);
  });

  draw.text(pages[0], [433.7, 248.8, 488.3, 266], calculation.householdSize);
  draw.amount(pages[0], [500.1, 165.1, 535.6, 182.2], calculation.line6);
  draw.amount(pages[1], [269.1, 687.3, 331.3, 702.2], calculation.healthUnder65);
  draw.text(pages[1], [269.1, 668.4, 295.7, 683.3], calculation.under65);
  draw.amount(pages[1], [269.1, 649.1, 331.3, 663.4], calculation.line7c);
  draw.amount(pages[1], [379.7, 649, 442, 663.3], calculation.line7c);
  draw.amount(pages[1], [269.1, 604.8, 331.3, 619.7], calculation.healthOver65);
  draw.text(pages[1], [269.1, 588.3, 295.7, 603.2], calculation.over65);
  draw.amount(pages[1], [269.1, 568.7, 331.3, 583.2], calculation.line7f);
  draw.amount(pages[1], [386.9, 568.9, 442, 583.4], calculation.line7f);
  draw.amount(pages[1], [381.3, 540.4, 439.6, 555.3], calculation.line7g);
  draw.amount(pages[1], [522.2, 540.7, 571, 555.6], calculation.line7g);
  draw.amount(pages[1], [522.2, 392.6, 571, 407.4], calculation.line8);
  draw.amount(pages[1], [381.8, 345.4, 434.6, 360.3], calculation.line9a);
  const homeRects = [
    [[79.4, 232.9, 248.3, 247.8], [280.3, 232.9, 333.6, 247.8]],
    [[79.4, 218.2, 248.3, 232.3], [280.3, 217.4, 333.6, 232.3]],
    [[79.4, 202.1, 248.3, 217], [280.5, 196, 333.7, 210.8]],
  ];
  homeRects.forEach(([nameRect, amountRect], index) => {
    const row = calculation.homeDebts[index];
    if (!row) return;
    draw.text(pages[1], nameRect, row.creditor_name);
    draw.amount(pages[1], amountRect, row.average_monthly_payment);
  });
  draw.amount(pages[1], [280.3, 177.2, 333.6, 192.1], calculation.line9b);
  draw.amount(pages[1], [384.8, 178, 438.1, 192.9], calculation.line9b);
  draw.amount(pages[1], [381.8, 138, 435.1, 151.7], calculation.line9c);
  draw.amount(pages[1], [522.2, 138.6, 571, 152.2], calculation.line9c);
  draw.amount(pages[1], [522.2, 107.5, 571, 121.2], calculation.line10);
  draw.text(pages[1], [123.7, 85, 507.7, 99.8], data.housing_adjustment_explanation || '');

  draw.check(pages[2], calculation.vehicleCount === 0
    ? [74.1, 704.5, 85.2, 714.7]
    : calculation.vehicleCount === 1
      ? [74.1, 692.7, 85.2, 702.9]
      : [74.1, 680.9, 85.2, 691.1]);
  draw.amount(pages[2], [522, 645.5, 570.9, 659.7], calculation.line12);
  const vehicles = (caseData?.vehicles || []).filter(vehicle => !vehicle.is_recreational).slice(0, 2);
  const vehicleName = vehicle => [vehicle?.year, vehicle?.make, vehicle?.model].filter(Boolean).join(' ');
  const vehicle1Description = calculation.line13b > 0
    ? vehicleName(vehicles[0]) || calculation.vehicle1Debts[0]?.property_description || 'Vehicle 1'
    : '';
  const vehicle2Description = calculation.line13e > 0
    ? vehicleName(vehicles[1]) || calculation.vehicle2Debts[0]?.property_description || 'Vehicle 2'
    : '';
  draw.text(pages[2], [203.3, 573.7, 519, 587.9], vehicle1Description);
  draw.amount(pages[2], [379.5, 533.6, 432.8, 547.7], calculation.line13a);
  const v1Rects = [
    [[83.7, 418, 238.3, 432.1], [261.3, 418, 319.1, 432.1]],
    [[84.3, 400.9, 238.9, 415], [261.9, 400.9, 319.7, 415]],
  ];
  v1Rects.forEach(([nameRect, amountRect], index) => {
    const row = calculation.vehicle1Debts[index];
    if (!row) return;
    draw.text(pages[2], nameRect, row.creditor_name);
    draw.amount(pages[2], amountRect, row.average_monthly_payment);
  });
  draw.amount(pages[2], [262.6, 377.3, 320.4, 391.5], calculation.line13b);
  draw.amount(pages[2], [379.5, 382.3, 432.8, 396.5], calculation.line13b);
  draw.amount(pages[2], [379.5, 348.1, 432.8, 362.3], calculation.line13c);
  draw.amount(pages[2], [521.7, 347.9, 570.6, 362], calculation.line13c);
  draw.text(pages[2], [203.3, 319.4, 493.5, 333.5], vehicle2Description);
  draw.amount(pages[2], [374.1, 278.1, 432.8, 292.3], calculation.line13d);
  const v2Rects = [
    [[82, 203.9, 237.6, 218], [261.3, 203.9, 319.1, 218]],
    [[82, 191.1, 237.7, 205.2], [261.3, 191.1, 319.1, 205.2]],
  ];
  v2Rects.forEach(([nameRect, amountRect], index) => {
    const row = calculation.vehicle2Debts[index];
    if (!row) return;
    draw.text(pages[2], nameRect, row.creditor_name);
    draw.amount(pages[2], amountRect, row.average_monthly_payment);
  });
  draw.amount(pages[2], [261.3, 166.5, 319.1, 180.7], calculation.line13e);
  draw.amount(pages[2], [379.5, 173.5, 435.5, 187.7], calculation.line13e);
  draw.amount(pages[2], [379.5, 137.1, 432.8, 151.3], calculation.line13f);
  draw.amount(pages[2], [521.7, 137.1, 570.6, 151.2], calculation.line13f);
  draw.amount(pages[2], [521.7, 98.7, 570.6, 112.9], calculation.line14);
  draw.amount(pages[2], [521.7, 58.9, 570.6, 73.1], calculation.line15);

  const necessaryKeys = ['taxes', 'involuntary_deductions', 'life_insurance', 'court_ordered_payments', 'education', 'childcare', 'additional_health_care', 'optional_telephones'];
  const necessaryRects = [
    [521.2, 671.2, 565.8, 682], [521.2, 616.9, 565.8, 627.7], [521.2, 571.3, 565.8, 582.1],
    [521.2, 541, 565.8, 551.8], [521.2, 500.5, 565.8, 511.3], [521.2, 463.5, 565.8, 474.3],
    [521.2, 420.5, 565.8, 431.3], [521.2, 366.1, 565.8, 376.9],
  ];
  necessaryKeys.forEach((key, index) => draw.amount(pages[3], necessaryRects[index], data[key]));
  draw.amount(pages[3], [521.2, 327.2, 565.8, 338], calculation.line24);
  draw.amount(pages[3], [244, 237.1, 288.5, 247.9], data.health_insurance);
  draw.amount(pages[3], [244, 219, 288.5, 229.8], data.disability_insurance);
  draw.amount(pages[3], [244.1, 202.5, 288.5, 213.3], data.health_savings_account);
  draw.amount(pages[3], [244, 188, 288.5, 198.8], calculation.healthInsuranceTotal);
  draw.amount(pages[3], [521.2, 188, 565.8, 198.8], calculation.line25);
  draw.check(pages[3], data.actually_spend_health_total === false
    ? [61.9, 156.4, 73.6, 166.6]
    : [61.9, 142.6, 73.6, 152.8]);
  if (data.actually_spend_health_total === false) draw.amount(pages[3], [244, 149.6, 288.5, 160.4], data.actual_health_total);
  draw.amount(pages[3], [521.2, 107.1, 565.8, 117.9], data.family_support_contributions);
  draw.amount(pages[3], [521.2, 65.3, 565.8, 76.1], data.family_violence);

  draw.amount(pages[4], [520.6, 692.3, 576.6, 706.5], data.additional_home_energy);
  draw.amount(pages[4], [520.6, 642, 576.6, 656.1], data.dependent_education);
  draw.amount(pages[4], [520.6, 563.3, 576.6, 577.5], calculation.line30);
  draw.amount(pages[4], [516.3, 482.3, 576.6, 496.5], data.charitable_contributions);
  draw.amount(pages[4], [514.7, 442.7, 576.6, 456.9], calculation.line32);
  draw.amount(pages[4], [392.8, 295.2, 450.6, 309.4], calculation.line9b);
  draw.amount(pages[4], [392.8, 256.3, 450.6, 270.5], calculation.line13b);
  draw.amount(pages[4], [392.8, 233.1, 450.6, 247.2], calculation.line13e);
  const otherRows = calculation.otherSecuredDebts.slice(0, 3);
  const otherRects = [
    [[90.7, 137, 226.8, 151.1], [234.6, 137, 328.8, 151.1], [333.1, 149.3, 344.2, 160.1], [333.1, 137.4, 344.2, 148.2], [392.8, 143.2, 450.6, 157.4]],
    [[90.7, 109.9, 226.8, 124], [234.6, 109.9, 328.8, 124], [333.1, 121.2, 344.2, 132], [333.1, 109.3, 344.2, 120.2], [392.8, 116, 450.6, 130.1]],
    [[90.7, 81.8, 226.8, 96], [234.6, 81.8, 328.8, 96], [333.1, 94.2, 344.2, 105], [333.1, 82.3, 344.2, 93.2], [392.8, 87.2, 450.6, 101.3]],
  ];
  otherRects.forEach(([nameRect, propertyRect, noRect, yesRect, amountRect], index) => {
    const row = otherRows[index];
    if (!row) return;
    draw.text(pages[4], nameRect, row.creditor_name);
    draw.text(pages[4], propertyRect, row.property_description);
    draw.check(pages[4], row.includes_taxes_insurance ? yesRect : noRect);
    draw.amount(pages[4], amountRect, row.average_monthly_payment);
  });
  draw.amount(pages[4], [392.8, 62, 450.6, 76.5], calculation.line33e);
  draw.amount(pages[4], [520.6, 62.2, 576.6, 76.7], calculation.line33e);

  const cureRows = (data.secured_debts || []).filter(row => numberValue(row.cure_total) > 0).slice(0, 3);
  draw.check(pages[5], cureRows.length ? [60.4, 669.1, 71.5, 680] : [60.4, 681.5, 71.5, 692.3]);
  const cureRects = [
    [[98.7, 604.3, 200, 618.4], [207.5, 604.3, 277.5, 618.4], [303.7, 603.9, 348.1, 618.1], [401.3, 603.9, 450.3, 618.1]],
    [[98.7, 576.7, 200, 590.8], [207.5, 576.7, 277.5, 590.8], [303.7, 576.2, 348.1, 590.4], [401.3, 576.2, 450.3, 590.4]],
    [[98.7, 550, 200, 564.2], [207.5, 550, 277.5, 564.2], [303.7, 549.7, 348.1, 563.8], [401.3, 549, 450.3, 563.1]],
  ];
  cureRects.forEach(([nameRect, propertyRect, totalRect, monthlyRect], index) => {
    const row = cureRows[index];
    if (!row) return;
    draw.text(pages[5], nameRect, row.creditor_name);
    draw.text(pages[5], propertyRect, row.property_description);
    draw.amount(pages[5], totalRect, row.cure_total);
    draw.amount(pages[5], monthlyRect, numberValue(row.cure_total) / 60);
  });
  draw.amount(pages[5], [401.3, 522.1, 450.3, 536.3], calculation.line34);
  draw.amount(pages[5], [519.5, 521.1, 578, 535.3], calculation.line34);
  draw.check(pages[5], calculation.priorityClaimTotal > 0 ? [60.4, 449.5, 71.5, 460.3] : [60.4, 461.7, 71.5, 472.5]);
  draw.amount(pages[5], [401.3, 419, 463.6, 433.2], calculation.priorityClaimTotal);
  draw.amount(pages[5], [519.5, 420, 578, 434.1], calculation.line35);
  draw.amount(pages[5], [401.3, 384.7, 463.6, 398.8], data.projected_chapter_13_payment);
  draw.text(pages[5], [401.5, 342.6, 428.1, 356.7], longFormMoney(calculation.adminMultiplier));
  draw.amount(pages[5], [401.3, 296.7, 463.6, 310.9], calculation.line36);
  draw.amount(pages[5], [519.5, 297.7, 578, 311.8], calculation.line36);
  draw.amount(pages[5], [519.5, 259.1, 578, 273.2], calculation.line37);
  draw.amount(pages[5], [401.5, 174.1, 463.8, 186.8], calculation.line24);
  draw.amount(pages[5], [401.2, 151.4, 463.5, 168.7], calculation.line32);
  draw.amount(pages[5], [402, 132.9, 464.2, 148.3], calculation.line37);
  draw.amount(pages[5], [401.3, 106.7, 463.6, 120.9], calculation.line38);
  draw.amount(pages[5], [519.5, 107.5, 578, 121.6], calculation.line38);

  draw.amount(pages[6], [522, 699.1, 573, 713.3], calculation.adjustedMonthlyIncome);
  draw.amount(pages[6], [394.5, 657.6, 453, 671.7], data.dependent_child_income);
  draw.amount(pages[6], [394.5, 608.9, 453, 623], data.retirement_contributions_and_loans);
  draw.amount(pages[6], [394.5, 571.2, 453, 585.3], calculation.line38);
  const special = (data.special_circumstances || []).slice(0, 3);
  const specialRects = [
    [[57.7, 471.5, 267.8, 485.6], [286.4, 471.5, 335.2, 485.6]],
    [[57.7, 450.5, 267.8, 464.7], [286.4, 451, 335.2, 464.1]],
    [[57.3, 432, 267.4, 446.1], [286.4, 432, 335.2, 446.1]],
  ];
  specialRects.forEach(([descriptionRect, amountRect], index) => {
    const row = special[index];
    if (!row) return;
    draw.text(pages[6], descriptionRect, row.description);
    draw.amount(pages[6], amountRect, row.amount);
  });
  draw.amount(pages[6], [286.4, 413.3, 335.2, 427.4], calculation.specialCircumstancesTotal);
  draw.amount(pages[6], [394.5, 412.8, 453, 426.9], calculation.specialCircumstancesTotal);
  draw.amount(pages[6], [394.5, 380.6, 453, 394.7], calculation.chapter13Adjustments);
  draw.amount(pages[6], [522, 380.6, 573, 394.7], calculation.chapter13Adjustments);
  draw.amount(pages[6], [522, 339.4, 573, 353.5], calculation.chapter13DisposableIncome);
  const changes = (data.income_expense_changes || []).slice(0, 4);
  const changeY = [179.1, 146.6, 112, 77.9];
  changes.forEach((row, index) => {
    const y = changeY[index];
    const checkboxBaseY = [173, 140.5, 106.9, 71.9][index];
    const topCheckboxY = [185.3, 152.8, 119.2, 84][index];
    draw.check(pages[6], row.form === '122C-2'
      ? [49.3, checkboxBaseY, 61, checkboxBaseY + 11.4]
      : [49.3, topCheckboxY, 61, topCheckboxY + 11.4]);
    draw.text(pages[6], [101.5, y, 125.9, y + 14.2], row.line || '');
    draw.text(pages[6], [137.7, y, 275.6, y + 14.2], row.reason || '');
    draw.text(pages[6], [284.2, y, 337.6, y + 14.2], formDate(row.date), { size: 7 });
    draw.check(pages[6], row.direction === 'decrease'
      ? [354.4, checkboxBaseY, 366.1, checkboxBaseY + 11.4]
      : [354.4, topCheckboxY, 366.1, topCheckboxY + 11.4]);
    draw.amount(pages[6], [419.9, y, 473.1, y + 14.2], row.amount);
  });

  draw.text(pages[7], [51.4, 646, 255.5, 660.2], debtor1 ? `/s/ ${debtor1}` : '');
  draw.text(pages[7], [301.9, 645.5, 505.8, 659.7], debtor2 ? `/s/ ${debtor2}` : '');
  draw.text(pages[7], [68.3, 607.9, 136.5, 622], date);
  draw.text(pages[7], [322.7, 607.9, 390.9, 622], debtor2 ? date : '');
};

export async function generateLongFormMeansTestPdf({
  caseData,
  dependents = [],
  expenses = [],
  creditors = [],
  meansTestIncomes = [],
  maritalAdjustments = [],
  longFormData = {},
  amended = false,
}) {
  const chapter = String(caseData?.chapter || '');
  if (!['7', '13'].includes(chapter)) throw new Error('Long-form means tests are only available for Chapter 7 or Chapter 13 cases.');
  try {
    await loadMeansTestStandards();
  } catch {
    // The standards loader retains the most recently cached or bundled values.
  }
  const preparedData = mergeLongFormData(
    longFormData,
    createDefaultLongFormData({ caseData, dependents, expenses, creditors })
  );
  const calculation = calculateLongFormMeansTest({
    caseData, dependents, meansTestIncomes, maritalAdjustments, longFormData: preparedData,
  });
  if (!calculation.aboveMedian) return null;

  const bytes = await fetchPdf(templateUrl(chapter));
  const pdf = await PDFDocument.load(bytes, { ignoreEncryption: true });
  removeAcroForm(pdf);
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const boldFont = await pdf.embedFont(StandardFonts.HelveticaBold);
  const draw = createDrawer(font);
  const args = { pdf, draw, caseData, data: preparedData, calculation, maritalAdjustments, amended };
  if (chapter === '7') drawChapter7(args);
  else drawChapter13(args);
  appendContinuationPages({
    pdf, font, boldFont, caseData, chapter, data: preparedData, calculation, maritalAdjustments,
  });
  return pdf;
}
